import React, { useEffect, useMemo, useState, useDeferredValue } from "react";
import { useTranslation } from "react-i18next";
import api, { isAuthError } from "@/lib/api";
import { authenticatedDownload, smartSaveOrShare } from "@/lib/download";
import { useAccounts } from "@/lib/AccountContext";
import { AccountSwitcher } from "@/components/AccountSwitcher";
import { usePdfViewer } from "@/lib/PdfViewerContext";
import { ReceiptPreview } from "@/components/DocumentPreview";
import { fmtMoney, fmtDateTime } from "@/lib/format";
import { detectCategory } from "@/lib/iban";
import TxDrawer from "@/components/TxDrawer";
import { Download, Search, ArrowDownLeft, ArrowUpRight, Eye, Loader2, Ban } from "lucide-react";
import { toast } from "sonner";

export default function Movements() {
  const { t, i18n } = useTranslation();
  const { openPdf } = usePdfViewer();
  const { selectedAccount } = useAccounts();
  const accId = selectedAccount?.id;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const deferredQ = useDeferredValue(q);
  const [kind, setKind] = useState("all");
  const [selected, setSelected] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);

  useEffect(() => {
    if (!accId) { setLoading(true); setRows([]); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/transactions", { params: { account_id: accId } });
        if (alive) setRows(data);
      } catch (e) {
        if (!isAuthError(e)) toast.error(t("movements.load_error"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [accId]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (kind === "credit" && r.amount <= 0) return false;
      if (kind === "debit" && r.amount > 0) return false;
      if (deferredQ) {
        const s = deferredQ.toLowerCase();
        if (![r.description, r.beneficiary_name, r.destination, r.reference].some((v) => (v || "").toLowerCase().includes(s))) return false;
      }
      return true;
    });
  }, [rows, deferredQ, kind]);

  const downloadReceipt = async (id, ref) => {
    if (downloadingId) return;
    const filename = `comprobante_${(ref || id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
    setDownloadingId(id);
    try {
      await smartSaveOrShare(`/documents/receipt/${id}?lang=${(i18n.language || "es").split("-")[0]}`, filename);
    } finally {
      setDownloadingId(null);
    }
  };

  const cancelScheduled = async (id) => {
    if (cancellingId) return;
    setCancellingId(id);
    try {
      await api.post(`/transfers/${id}/cancel`);
      toast.success(t("movements.cancel_success", { defaultValue: "Transferencia cancelada" }));
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status: "cancelled" } : r)));
      setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setCancellingId(null);
    }
  };
  const viewReceipt = (tx) => openPdf({
    content: <ReceiptPreview tx={tx} />,
    title: `Comprobante ${tx.reference || ""}`.trim(),
    path: `/documents/receipt/${tx.id}?lang=${(i18n.language || "es").split("-")[0]}`,
    filename: `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`,
  });

  return (
    <div className="space-y-6 max-w-7xl" data-testid="movements-page">
      <div>
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">{t("movements.eyebrow")}</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">{t("movements.title")}</h1>
        <p className="text-slate-500 mt-1">{t("movements.subtitle")}</p>
      </div>

      <AccountSwitcher />

      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
        <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-slate-100">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("movements.search_placeholder")}
              data-testid="movements-search"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100 outline-none" />
          </div>
          <div className="flex bg-slate-100 rounded-full p-1">
            {[
              { k: "all", label: t("movements.filter_all") },
              { k: "credit", label: t("movements.filter_credit") },
              { k: "debit", label: t("movements.filter_debit") },
            ].map(({ k, label }) => (
              <button key={k} onClick={() => setKind(k)} data-testid={`movements-filter-${k}`}
                className={`px-4 py-2 rounded-full text-sm transition-all ${kind === k ? "bg-white text-slate-900 shadow font-semibold" : "text-slate-600 hover:text-slate-900"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="divide-y divide-slate-50">
          {loading && (
            <div data-testid="movements-loading">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-slate-100 animate-pulse shrink-0"></div>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="h-3.5 rounded bg-slate-100 animate-pulse" style={{ width: `${55 + (i % 3) * 12}%` }}></div>
                    <div className="h-3 rounded bg-slate-100 animate-pulse w-1/3"></div>
                  </div>
                  <div className="h-4 w-20 rounded bg-slate-100 animate-pulse shrink-0"></div>
                </div>
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="px-6 py-16 text-center text-slate-500" data-testid="movements-empty">{t("movements.empty")}</div>
          )}
          {!loading && filtered.map((r) => {
            const cat = detectCategory(r.description);
            return (
              <div key={r.id} onClick={() => setSelected(r)} data-testid={`tx-row-${r.id}`}
                   className="px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 hover:bg-slate-50 cursor-pointer transition-colors">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: cat.bg, color: cat.color }}>
                  {r.amount > 0 ? <ArrowDownLeft size={20} /> : <ArrowUpRight size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    <span>{r.description}</span>
                    {r.status === "pending" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-orange-100 text-orange-700">{t("movements.pending")}</span>
                    )}
                    {r.status === "rejected" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-red-100 text-red-700">{t("movements.rejected")}</span>
                    )}
                    {r.status === "scheduled" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-blue-100 text-blue-700">{t("movements.scheduled")}</span>
                    )}
                    {r.status === "refunded" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-purple-100 text-purple-700">{t("movements.refunded")}</span>
                    )}
                    {r.status === "cancelled" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-slate-200 text-slate-700">{t("movements.cancelled", { defaultValue: "Cancelada" })}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: cat.bg, color: cat.color }}>{t(`tx.cat.${cat.key}`)}</span>
                    {r.transfer_type && <span className="font-mono-fin">{t(`tx.type.${r.transfer_type}`, { defaultValue: r.transfer_type })}</span>}
                    <span>·</span>
                    <span>{fmtDateTime(r.executed_at || r.created_at)}</span>
                    <span>·</span>
                    <span className="font-mono-fin">{r.reference}</span>
                  </div>
                  {r.status === "rejected" && r.rejection_reason && (
                    <div className="text-[11px] text-red-700 mt-1">↳ {t("movements.reason")}: {r.rejection_reason}</div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono-fin text-sm sm:text-base font-bold whitespace-nowrap ${r.amount > 0 ? "text-green-600" : "text-slate-900"}`}>
                    {r.amount > 0 ? "+" : ""}{fmtMoney(r.amount, r.currency)}
                  </div>
                  <div className="mt-1 flex items-center gap-1 justify-end">
                    <button
                      onClick={(e) => { e.stopPropagation(); viewReceipt(r); }}
                      data-testid={`view-receipt-${r.id}`}
                      title={t("movements.view")}
                      className="px-2 py-1 rounded-full text-xs text-slate-600 hover:text-blue-600 hover:bg-blue-50 inline-flex items-center gap-1"
                    >
                      <Eye size={12} /> {t("movements.view")}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); downloadReceipt(r.id, r.reference); }}
                      data-testid={`download-receipt-${r.id}`}
                      disabled={downloadingId === r.id}
                      title={t("movements.pdf")}
                      className="px-2 py-1 rounded-full text-xs text-blue-600 hover:bg-blue-50 inline-flex items-center gap-1 font-semibold disabled:opacity-50"
                    >
                      {downloadingId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} {t("movements.pdf")}
                    </button>
                    {r.status === "scheduled" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); cancelScheduled(r.id); }}
                        data-testid={`cancel-scheduled-${r.id}`}
                        disabled={cancellingId === r.id}
                        title={t("movements.cancel", { defaultValue: "Cancelar" })}
                        className="px-2 py-1 rounded-full text-xs text-red-600 hover:bg-red-50 inline-flex items-center gap-1 font-semibold disabled:opacity-50"
                      >
                        {cancellingId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />} {t("movements.cancel", { defaultValue: "Cancelar" })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TxDrawer tx={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
